import { GroupSecurity } from '@quromedical/fhir-common';
import type { CareTeam, CareTeamSearchParams } from '@quromedical/models';
import { CrudForm } from 'components/form';
import { WithGroup } from 'core/auth';
import {
  CareTeamCreateCard,
  columns,
  getCareTeamDataFromResponse,
  initialSearch,
  searchFields,
} from 'core/care-team';
import { PaginatedTable } from 'core/table';
import { Section, ToggleCard } from 'design-system';
import { router } from 'expo-router';
import { useResponsive } from 'hooks/useResponsive';
import { organizationFetcher } from 'integration';
import { CareTeamApi } from 'integration/aggregate';
import type { GroupStackScreenProps } from 'navigation';
import type React from 'react';
import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { strings } from 'strings';

const api = new CareTeamApi();

export const CareTeamListScreen: React.FC<
  GroupStackScreenProps<'List'>
> = () => {
  const [search, setSearch] = useState<CareTeamSearchParams>(initialSearch);

  const isStriped = useResponsive({ md: true }, false);

  const fetcher = () => api.getAllCareTeams(search);

  const orgFetcher = organizationFetcher();

  const onRowPress = useCallback((row: Partial<CareTeam>) => {
    const id = row.id;
    if (!id) return;

    router.push(`/admin/care-team/${id}`);
  }, []);

  return (
    <ScrollView>
      <WithGroup groups={[GroupSecurity.DeviceCreate]}>
        <Section unsetZIndex>
          <ToggleCard
            icon="people-outline"
            title={strings.CareTeamToggleCardTitle}
            textExpand={strings.CareTeamToggleCardExpandButtonText}
            textCollapse={strings.CareTeamToggleCardCollapseButtonText}
            contentWrapperProps={{ paddingHorizontal: 0, paddingVertical: 0 }}
          >
            <CareTeamCreateCard organizationFetcher={orgFetcher} />
          </ToggleCard>
        </Section>
      </WithGroup>
      <Section unsetZIndex>
        <CrudForm
          fields={searchFields}
          initialValues={initialSearch}
          showSubmitButton={false}
          onChange={setSearch}
        />
      </Section>
      <Section>
        <PaginatedTable
          cacheKey={`CareTeam${JSON.stringify(search)}`}
          fetcher={fetcher}
          columns={columns}
          showHeader={true}
          isStriped={isStriped}
          getDataFromResponse={getCareTeamDataFromResponse}
          onRowPress={onRowPress}
        />
      </Section>
    </ScrollView>
  );
};
